import { X } from "lucide-react";
import React, { useEffect, useRef } from "react";
import ProductShareGatePanel, { GATE_EMAIL_INPUT_ID } from "./ProductShareGatePanel";
import { useModalFocusTrap } from "./useModalFocusTrap";
import type { ShareGateFlow } from "../hooks/useProductShareGateFlow";

export interface ProductShareGateModalProps {
  flow: ShareGateFlow;
  total: number;
  onLoginClick: () => void;
}

/**
 * The gate panel lifted off the page (V5D). Any sign-up button opens it on the
 * email step; a submitted email moves it to the code step; closing it, by the
 * cross, the backdrop or Escape, returns to the email step with values kept.
 */
const ProductShareGateModal: React.FC<ProductShareGateModalProps> = ({ flow, total, onLoginClick }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { isModalOpen, closeModal, step } = flow;

  useModalFocusTrap({ dialogRef, isOpen: isModalOpen, onClose: () => closeModal() });

  // Runs after the focus trap's own effect (declaration order), so the field
  // for the current step gets the cursor rather than the first focusable.
  useEffect(() => {
    if (!isModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const selector = step === "code" ? "#gate-otp-m" : `#${GATE_EMAIL_INPUT_ID}-m`;
    dialogRef.current?.querySelector<HTMLInputElement>(selector)?.focus({ preventScroll: true });
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isModalOpen, step]);

  if (!isModalOpen) return null;

  return (
    <div
      data-testid="product-share-gate-modal"
      className="fixed inset-0 z-[300] flex items-end justify-center bg-v7-ink/30 pt-14 backdrop-blur-md sm:items-center sm:p-4"
      onMouseDown={(event) => { if (event.target === event.currentTarget) closeModal("gate_modal_backdrop"); }}
    >
      <div ref={dialogRef} role="dialog" aria-modal="true"
        aria-label={step === "code" ? "Enter your code" : "Sign up free"}
        className="relative w-[min(1120px,100%)]">
        <button
          type="button"
          onClick={() => closeModal()}
          aria-label="Close"
          className="absolute -top-11 right-0 grid h-9 w-9 place-items-center rounded-full bg-v7-ink/35 text-v7-white/85 transition-colors hover:bg-v7-ink/60 hover:text-v7-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-white"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
        <div
          className="max-h-[calc(100dvh-56px)] overflow-y-auto overscroll-contain rounded-t-[20px] border border-v7-ink/15 bg-v7-paper pb-[env(safe-area-inset-bottom)] shadow-2xl sm:rounded-v7-panel [&>div]:border-t-0"
        >
          <ProductShareGatePanel flow={flow} total={total} placement="modal" onLoginClick={onLoginClick} />
        </div>
      </div>
    </div>
  );
};

export default ProductShareGateModal;
